import { Home, NavigateNext } from '@mui/icons-material';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import React from 'react';

interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  isLast?: boolean;
  disabled?: boolean;
}

interface AutoBreadcrumbProps {
  pathname?: string;
  customLabels?: Record<string, string>;
  homeLabel?: string;
  maxItems?: number;
  separator?: React.ReactNode;
  onNavigate?: (path: string) => void;
  disabledPaths?: string[];
  hiddenPaths?: string[];
}

const AutoBreadcrumb: React.FC<AutoBreadcrumbProps> = ({
  pathname = '/dashboard',
  customLabels = {},
  homeLabel = 'Dashboard',
  maxItems = 8,
  separator = <NavigateNext fontSize="small" />,
  onNavigate,
  disabledPaths = ['edit', 'detail'],
  hiddenPaths = ['dashboard']
}) => {
  const formatLabel = (str: string): string => {
    return str
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const isDashboardPage = pathname === '/dashboard' || pathname === '/';

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = pathname.split('/').filter((x) => x);

    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: homeLabel,
        path: '/',
        // icon: (
        //   <Box
        //     component="span"
        //     sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}
        //   >
        //     {iconSvg.dashboard}
        //   </Box>
        // ),
        icon: <Home sx={{ mr: 0.5, fontSize: 20 }} />,
        disabled: isDashboardPage
      }
    ];

    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;

      if (hiddenPaths.includes(path.toLowerCase())) {
        return;
      }

      const label = customLabels[path] || formatLabel(path);
      const isDisabled = disabledPaths.includes(path.toLowerCase());

      breadcrumbs.push({
        label,
        path: currentPath,
        isLast: index === paths.length - 1,
        disabled: isDisabled
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleClick = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      console.log('Navigate to:', path);
    }
  };

  return (
    <Breadcrumbs
      maxItems={maxItems}
      separator={separator}
      aria-label="breadcrumb"
      sx={{ mb: 2 }}
    >
      {breadcrumbs.map((crumb) => {
        if (crumb.isLast || crumb.disabled) {
          return (
            <Typography
              key={crumb.path}
              color={crumb.isLast ? 'text.primary' : 'text.secondary'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: crumb.isLast ? 500 : 400
              }}
            >
              {crumb.icon}
              {crumb.label}
            </Typography>
          );
        }

        return (
          <Link
            key={crumb.path}
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'inherit'
            }}
            color="inherit"
            onClick={() => handleClick(crumb.path)}
          >
            {crumb.icon}
            {crumb.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default AutoBreadcrumb;

// ============================================
// CONTOH 1: Penggunaan Dasar
// ============================================
// const BasicExample: React.FC = () => {
//   return (
//     <Container>
//       <AutoBreadcrumb pathname="/dashboard/users/profile" />
//       <Typography>Konten halaman...</Typography>
//     </Container>
//   );
// };

// // ============================================
// // CONTOH 2: Dengan Custom Labels
// // ============================================
// const CustomLabelsExample: React.FC = () => {
//   const customLabels = {
//     'dashboard': 'Dasbor',
//     'users': 'Pengguna',
//     'profile': 'Profil Saya',
//     'settings': 'Pengaturan',
//     'user-management': 'Manajemen Pengguna'
//   };

//   return (
//     <Container>
//       <AutoBreadcrumb
//         pathname="/dashboard/user-management/settings"
//         customLabels={customLabels}
//         homeLabel="Beranda"
//       />
//       <Typography>Konten halaman...</Typography>
//     </Container>
//   );
// };

// // ============================================
// // CONTOH 3: Dengan React Router
// // ============================================
// const WithRouterExample: React.FC = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const handleNavigate = (path: string) => {
//     navigate(path);
//   };

//   return (
//     <Container>
//       <AutoBreadcrumb
//         pathname={location.pathname}
//         onNavigate={handleNavigate}
//         customLabels={{
//           'products': 'Produk',
//           'electronics': 'Elektronik',
//           'laptops': 'Laptop'
//         }}
//       />
//       <Typography>Konten halaman...</Typography>
//     </Container>
//   );
// };

// // ============================================
// // CONTOH 4: Dengan Custom Separator & Max Items
// // ============================================
// const CustomizedExample: React.FC = () => {
//   return (
//     <Container>
//       <AutoBreadcrumb
//         pathname="/dashboard/reports/sales/monthly/2024/january"
//         separator="/"
//         maxItems={5}
//         customLabels={{
//           'dashboard': 'Dasbor',
//           'reports': 'Laporan',
//           'sales': 'Penjualan',
//           'monthly': 'Bulanan',
//           'january': 'Januari'
//         }}
//       />
//       <Typography>Konten halaman...</Typography>
//     </Container>
//   );
// };

// // ============================================
// // CONTOH 5: Dynamic Path (Complete App Example)
// // ============================================
// const App: React.FC = () => {
//   const location = useLocation();
//   const navigate = useNavigate();

//   // Custom labels untuk seluruh aplikasi
//   const appLabels = {
//     'dashboard': 'Dasbor',
//     'products': 'Produk',
//     'categories': 'Kategori',
//     'orders': 'Pesanan',
//     'customers': 'Pelanggan',
//     'settings': 'Pengaturan',
//     'profile': 'Profil',
//     'add': 'Tambah Baru',
//     'edit': 'Edit',
//     'view': 'Lihat Detail'
//   };

//   return (
//     <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
//       <Container maxWidth="lg">
//         {/* Breadcrumb otomatis mengikuti route saat ini */}
//         <AutoBreadcrumb
//           pathname={location.pathname}
//           customLabels={appLabels}
//           onNavigate={(path) => navigate(path)}
//           homeLabel="Home"
//         />

//         {/* Main content */}
//         <Box sx={{ mt: 3 }}>
//           {/* Router Outlet atau konten lainnya */}
//         </Box>
//       </Container>
//     </Box>
//   );
// };

// // ============================================
// // CONTOH 6: Dengan ID Dinamis
// // ============================================
// const DynamicIdExample: React.FC = () => {
//   // Misalnya path: /dashboard/users/12345/edit
//   const userId = '12345';
//   const userName = 'John Doe';

//   const customLabels = {
//     'dashboard': 'Dasbor',
//     'users': 'Daftar Pengguna',
//     [userId]: userName, // ID dinamis diganti dengan nama
//     'edit': 'Edit Pengguna'
//   };

//   return (
//     <Container>
//       <AutoBreadcrumb
//         pathname={`/dashboard/users/${userId}/edit`}
//         customLabels={customLabels}
//       />
//       <Typography>Edit form untuk {userName}...</Typography>
//     </Container>
//   );
// };

// // ============================================
// // CONTOH 7: Multi-level dengan Data dari API
// // ============================================
// const ApiDataExample: React.FC = () => {
//   const [labels, setLabels] = React.useState<Record<string, string>>({});

//   React.useEffect(() => {
//     // Simulasi fetch data dari API
//     const fetchLabels = async () => {
//       // const response = await fetch('/api/breadcrumb-labels');
//       // const data = await response.json();

//       // Simulasi data
//       const data = {
//         'dashboard': 'Dashboard',
//         'reports': 'Laporan',
//         'financial': 'Keuangan',
//         'quarterly': 'Per Kuartal',
//         'q1-2024': 'Q1 2024'
//       };

//       setLabels(data);
//     };

//     fetchLabels();
//   }, []);

//   return (
//     <Container>
//       <AutoBreadcrumb
//         pathname="/dashboard/reports/financial/quarterly/q1-2024"
//         customLabels={labels}
//       />
//       <Typography>Laporan keuangan Q1 2024...</Typography>
//     </Container>
//   );
// };
